import React, { useEffect, useRef, useState } from "react";
import { UserCheck, PenLine } from "lucide-react";
import TspApiService from "../../api/tspApiService";

const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;

// Team Leader input for Add / Edit Journey. The leader can be:
//  - picked from the suggestions: existing ICC users with TSP access (the server
//    decides who is listed, and checks again on save). Picking stores that
//    user's id — the leader is then a real account and can open the journey;
//  - typed by hand: any name. Stored as plain text only — it is not an account,
//    and it is never matched to one by guessing.
// Typing after a pick turns the entry back into a hand-typed name.
//
// value: { name, userId } (userId "" for a typed name); onChange gets the same shape.
export default function TspLeaderField({ id, value, onChange, disabled, invalid, describedBy, maxLength }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [searching, setSearching] = useState(false);
  const typedRef = useRef(false);
  const listId = `${id}-suggestions`;

  const query = (value.name || "").trim();
  const linked = Boolean(value.userId);

  useEffect(() => {
    // Only search for what the person is typing, never for a picked name.
    if (!typedRef.current || linked || query.length < MIN_QUERY) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }
    let current = true;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await TspApiService.searchJourneyLeaders(query);
        if (!current) return;
        const users = Array.isArray(res?.data) ? res.data : [];
        setSuggestions(users);
        setActive(-1);
        setOpen(users.length > 0);
      } catch {
        // No suggestions: the name can still be typed by hand.
        if (current) {
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        if (current) setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [query, linked]);

  const pick = (user) => {
    typedRef.current = false;
    setOpen(false);
    setSuggestions([]);
    onChange({ name: user.name || "", userId: String(user.id) });
  };

  const handleKeyDown = (event) => {
    if (!open || suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
    } else if (event.key === "Enter" && active >= 0) {
      event.preventDefault();
      pick(suggestions[active]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="tsp-leader-field">
      <input
        id={id}
        type="text"
        className="tsp-field-control"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        aria-required="true"
        aria-invalid={invalid ? "true" : undefined}
        aria-describedby={describedBy}
        value={value.name || ""}
        maxLength={maxLength}
        placeholder="Search a devotee, or type the leader's name"
        autoComplete="off"
        disabled={disabled}
        onChange={(event) => {
          typedRef.current = true;
          onChange({ name: event.target.value, userId: "" });
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => setOpen(false)}
        onFocus={() => setOpen(suggestions.length > 0)}
      />
      {open && (
        <ul id={listId} role="listbox" className="tsp-leader-suggestions" aria-label="Devotees with TSP access">
          {suggestions.map((user, index) => (
            <li
              key={user.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={`tsp-leader-suggestion ${index === active ? "is-active" : ""}`}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(user);
              }}
            >
              <span className="tsp-leader-suggestion-name">{user.name}</span>
              {user.phone_hint && <span className="tsp-leader-suggestion-sub">{user.phone_hint}</span>}
            </li>
          ))}
        </ul>
      )}
      {query && (
        <span className={`tsp-leader-mode ${linked ? "is-linked" : ""}`} data-testid="tsp-leader-mode">
          {linked ? (
            <>
              <UserCheck size={12} aria-hidden="true" /> Registered devotee — can open this journey
            </>
          ) : searching ? (
            "Searching devotees…"
          ) : (
            <>
              <PenLine size={12} aria-hidden="true" /> Typed name — not linked to a devotee account
            </>
          )}
        </span>
      )}
    </div>
  );
}
